// MasterViewPage が参照するマスタ定義 (MASTER_DEFS) と関連する型・解決関数。

import { ReactNode } from "react";
import { Stack, Typography, Chip } from "@mui/material";
import { ExtendedDB } from "../../../types/extendedDB";
import { MasterLink, DataEntityType, MasterEntityType } from "../../../components/links/EntityLinks";
import type { WorkBase } from "../../../schema/generated/Work";
import type { AreaDivisionBase } from "../../../schema/generated/AreaDivision";
import type { MaterialDivisionBase } from "../../../schema/generated/MaterialDivision";
import type { ClientBase } from "../../../schema/generated/Client";
import type { WorkTypeBase } from "../../../schema/generated/WorkType";
import type { WorkSubtypeBase } from "../../../schema/generated/WorkSubtype";
import type { WorkDetailBase } from "../../../schema/generated/WorkDetail";
import type { ConstructionMethodBase } from "../../../schema/generated/ConstructionMethod";
import type { StirringMethodBase } from "../../../schema/generated/StirringMethod";
import type { CarryInLocationBase } from "../../../schema/generated/CarryInLocation";
import type { LocationDivisionBase } from "../../../schema/generated/LocationDivision";
import type { QualityMeasurementItemBase } from "../../../schema/generated/QualityMeasurementItem";
import type { MaterialMasterBase } from "../../../schema/generated/MaterialMaster";
import type { MaterialDetailBase } from "../../../schema/generated/MaterialDetail";
import type { PotLifeSpecBase } from "../../../schema/generated/PotLifeSpec";
import type { MixRatioDefinitionBase } from "../../../schema/generated/MixRatioDefinition";
import {
  findById, matName, wtName, wstName, wdName, cmName, smName, adName, cliName, mdivName, mdName,
  mixRatioDisplayName, mixLabelOrNull, slotLabelFor, formatParentPath, checkOrDash, checkOrCross,
  linkOr, rangeOrDash, activeChildrenBy,
} from "./masterViewHelpers";

// =====================================================================
// 子リスト定義
//
// 各 def は masterDef<行型>() / childList() で宣言時に行型を検査し、
// MASTER_DEFS への格納時は消去型(MasterViewRecord)へ落とす。
// MasterViewPage は collectionForMasterKey(masterKey) の行しか渡さないため、
// 宣言時の対応(masterKey ↔ 行型)が実行時の安全性を保証する。
// =====================================================================

// 閲覧対象レコード・子リスト行の消去型(格納時の上限型)。
export type MasterViewRecord = { id: number | string };

export type ChildColumn = {
  label: string;
  width?: number | string;
  // メソッド構文(バイバリアント検査)にすることで、行型付き定義からの無キャスト代入を成立させる
  render(row: MasterViewRecord, db: ExtendedDB): ReactNode;
};

export type ChildListDef = {
  key: string;
  label: string;
  rows(record: MasterViewRecord, db: ExtendedDB): MasterViewRecord[];
  columns: ChildColumn[];
  // 先頭列を MasterLink/DataLink でラップする方式を切り替える
  kind?: "master" | "data";
  // master kind: 行 ID から飛ぶマスタ閲覧先
  detailMasterKey?: MasterEntityType;
  // data kind: DataLink type
  dataEntityType?: DataEntityType;
  dataParentId?(row: MasterViewRecord, record: MasterViewRecord): string | number | undefined;
  empty?: string;
};

export type MasterDef = {
  title: string;
  // masterKey に対応する行集合(単一情報源。旧 collectionForMasterKey の switch を置き換える)
  collection(db: ExtendedDB): readonly MasterViewRecord[];
  recordTitle(record: MasterViewRecord, db: ExtendedDB): string;
  infoFields: { label: string; render(record: MasterViewRecord, db: ExtendedDB): ReactNode }[];
  childLists?: ChildListDef[];
};

// ---- 宣言用(行型付き)の型とヘルパ ----

type ChildColumnOf<TRow> = {
  label: string;
  width?: number | string;
  render(row: TRow, db: ExtendedDB): ReactNode;
};

type ChildListOf<TRecord, TRow extends MasterViewRecord> = {
  key: string;
  label: string;
  rows(record: TRecord, db: ExtendedDB): TRow[];
  columns: ChildColumnOf<TRow>[];
  kind?: "master" | "data";
  detailMasterKey?: MasterEntityType;
  dataEntityType?: DataEntityType;
  dataParentId?(row: TRow, record: TRecord): string | number | undefined;
  empty?: string;
};

type MasterDefOf<TRecord extends MasterViewRecord> = {
  title: string;
  // 行集合アクセサ。宣言時に TRecord と実コレクションの行型一致が型検査される(対応宣言の機械検査)。
  collection(db: ExtendedDB): readonly TRecord[];
  recordTitle(record: TRecord, db: ExtendedDB): string;
  infoFields: { label: string; render(record: TRecord, db: ExtendedDB): ReactNode }[];
  childLists?: ChildListDef[];
};

// 子リスト定義を行型付きで検査してから消去型へ落とす(rows の record 引数の型注釈から行型が決まる)。
// コールバックはメソッド構文(バイバリアント検査)のため、消去型へ表明なしで代入できる。
function childList<TRecord extends MasterViewRecord, TRow extends MasterViewRecord>(def: ChildListOf<TRecord, TRow>): ChildListDef {
  return def;
}

// マスタ定義を行型付きで検査してから消去型へ落とす。
// コールバックはメソッド構文(バイバリアント検査)のため、消去型へ表明なしで代入できる。
function masterDef<TRecord extends MasterViewRecord>(def: MasterDefOf<TRecord>): MasterDef {
  return def;
}

// =====================================================================
// MASTER_DEFS
// =====================================================================

export const MASTER_DEFS: Record<string, MasterDef> = {
  // ---------------- 工事 ----------------
  work: masterDef<WorkBase>({
    collection: (db) => db.works,
    title: "工事",
    recordTitle: (r) => r.short_name || r.official_name || `工事 #${r.id}`,
    infoFields: [
      { label: "ID", render: (r) => `#${r.id}` },
      { label: "整理番号", render: (r) => r.control_number || "-" },
      { label: "略称", render: (r) => r.short_name || "-" },
      { label: "正式名称", render: (r) => r.official_name || "-" },
      { label: "工事型区分", render: (r, db) => linkOr("area-division", r.area_division_id, adName(db, r.area_division_id)) },
      { label: "発注者", render: (r, db) => linkOr("client", r.client_id, cliName(db, r.client_id)) },
      { label: "発注者部署", render: (r) => r.client_department || "-" },
      { label: "受注者", render: (r) => r.contractor_name || "-" },
      { label: "工事場所", render: (r) => r.location || "-" },
      { label: "工期", render: (r) => `${r.period_start || "-"} 〜 ${r.period_end || "-"}` },
      { label: "塗装種類", render: (r) => r.paint_kind || "-" },
      { label: "構造形式", render: (r) => r.structure_form || "-" },
      { label: "環境区分", render: (r) => r.environment_category || "-" },
      { label: "高架下条件", render: (r) => r.overhead_condition || "-" },
    ],
    childLists: [
      childList({
        key: "areas",
        label: "施工箇所",
        kind: "data",
        dataEntityType: "plan-area",
        rows: (r: WorkBase, db) => db.constructionPlanAreas.filter((a) => a.deleted_at == null && a.work_id === r.id),
        columns: [
          { label: "工種", render: (r, db) => linkOr("work-type", r.work_type_id, wtName(db, r.work_type_id)) },
          { label: "種別", render: (r, db) => linkOr("work-subtype", r.work_subtype_id, wstName(db, r.work_subtype_id)) },
          { label: "工程", render: (r, db) => {
            const wds = db.workDetails
              .filter((d) => d.deleted_at == null && d.work_subtype_id === r.work_subtype_id)
              .sort((a, b) => a.id - b.id);
            if (wds.length === 0) {
              return <Typography variant="caption" color="text.secondary">工程なし</Typography>;
            }
            return (
              <Stack direction="column" gap={0.5}>
                {wds.map((wd) => {
                  const completed = db.processExecutions.some(
                    (p) => p.deleted_at == null && p.construction_plan_area_id === r.id && p.work_detail_id === wd.id && p.completed_at != null,
                  );
                  const mixLabel = mixLabelOrNull(db, wd.mix_ratio_definition_id);
                  return (
                    <Stack key={wd.id} direction="row" gap={0.5} alignItems="center" flexWrap="wrap">
                      <MasterLink type="work-detail" id={wd.id} name={
                        <Chip size="small" clickable variant={completed ? "filled" : "outlined"} color={completed ? "success" : "default"} label={wd.name} />
                      } />
                      {mixLabel !== null && (
                        <>
                          <Typography variant="caption" color="text.secondary">/</Typography>
                          <MasterLink type="mix-ratio" id={wd.mix_ratio_definition_id} name={
                            <Chip size="small" clickable variant="outlined" label={mixLabel} />
                          } />
                        </>
                      )}
                    </Stack>
                  );
                })}
              </Stack>
            );
          } },
          // 施工箇所行は unit フィールドを持たない(旧実装の r.unit は常に undefined で空文字へ
          // フォールバックしていた)ため、数量のみを表示する。
          { label: "予定数量", render: (r) => r.planned_qty != null ? `${r.planned_qty}` : "-" },
          { label: "予定期間", render: (r) => `${r.planned_start ?? "-"} 〜 ${r.planned_end ?? "-"}` },
        ],
        empty: "この工事に紐付く施工箇所はまだありません",
      }),
    ],
  }),

  // ---------------- 工事型区分 ----------------
  "area-division": masterDef<AreaDivisionBase>({
    collection: (db) => db.areaDivisions,
    title: "工事型区分",
    recordTitle: (r) => `#${r.id} ${r.name}`,
    infoFields: [
      { label: "ID", render: (r) => `#${r.id}` },
      { label: "名称", render: (r) => r.name },
      { label: "説明", render: (r) => r.description ?? "-" },
      { label: "表示順", render: (r) => r.display_order ?? "-" },
      { label: "有効", render: (r) => checkOrCross(r.is_active) },
      {
        label: "場所ラベル",
        render: (r, db) => {
          const labels = db.areaDivisionLocationLabels
            .filter((l) => l.deleted_at == null && l.area_division_id === r.id)
            .sort((a, b) => a.slot - b.slot);
          if (labels.length === 0) {
            return "-";
          }
          return (
            <Stack direction="row" gap={0.5} flexWrap="wrap">
              {labels.map((l) => (
                <Chip key={l.id} size="small" variant="outlined" label={`${l.slot}: ${l.label_name}`} />
              ))}
            </Stack>
          );
        },
      },
    ],
    childLists: [
      childList({
        key: "works",
        label: "紐付く工事",
        detailMasterKey: "work",
        rows: (r: AreaDivisionBase, db) => activeChildrenBy(db.works, "area_division_id", r.id),
        columns: [
          { label: "整理番号", render: (r) => r.control_number ?? "-" },
          { label: "略称", render: (r) => r.short_name ?? "-" },
          { label: "工期", render: (r) => `${r.period_start ?? "-"} 〜 ${r.period_end ?? "-"}` },
        ],
      }),
      childList({
        key: "workTypes",
        label: "工種",
        detailMasterKey: "work-type",
        rows: (r: AreaDivisionBase, db) => activeChildrenBy(db.workTypes, "area_division_id", r.id),
        columns: [{ label: "工種名", render: (r) => r.name }],
      }),
      childList({
        key: "locationDivisions",
        label: "場所区分",
        detailMasterKey: "location-division",
        rows: (r: AreaDivisionBase, db) => activeChildrenBy(db.locationDivisions, "area_division_id", r.id),
        columns: [
          { label: "スロット", render: (r, db) => slotLabelFor(db, r.area_division_id, r.slot) },
          { label: "上位区分パス", render: (r, db) => formatParentPath(db, r.id) || "-" },
          { label: "区分名", render: (r) => r.division_name },
        ],
      }),
      childList({
        key: "carryInLocations",
        label: "搬入場所",
        detailMasterKey: "carry-in-location",
        rows: (r: AreaDivisionBase, db) => activeChildrenBy(db.carryInLocations, "area_division_id", r.id),
        columns: [{ label: "搬入場所名", render: (r) => r.name }],
      }),
      childList({
        key: "qualityMeasurementItems",
        label: "品質測定項目",
        detailMasterKey: "quality-measurement-item",
        rows: (r: AreaDivisionBase, db) => activeChildrenBy(db.qualityMeasurementItems, "area_division_id", r.id),
        columns: [
          { label: "項目名", render: (r) => r.name },
          { label: "単位", render: (r) => r.unit ?? "-" },
          { label: "対象", render: (r, db) => renderQualityScope(r, db) },
          { label: "上下限", render: (r) => rangeOrDash(r.lower_limit, r.upper_limit) },
        ],
      }),
    ],
  }),

  // ---------------- 材料区分 ----------------
  "material-division": masterDef<MaterialDivisionBase>({
    collection: (db) => db.materialDivisions,
    title: "材料区分",
    recordTitle: (r) => `#${r.id} ${r.name}`,
    infoFields: [
      { label: "ID", render: (r) => `#${r.id}` },
      { label: "名称", render: (r) => r.name },
      { label: "説明", render: (r) => r.description ?? "-" },
      { label: "表示順", render: (r) => r.display_order ?? "-" },
      { label: "有効", render: (r) => checkOrCross(r.is_active) },
    ],
    childLists: [
      childList({
        key: "materials",
        label: "材料",
        detailMasterKey: "material",
        rows: (r: MaterialDivisionBase, db) => activeChildrenBy(db.materials, "material_division_id", r.id),
        columns: [
          { label: "材料名", render: (r) => r.name },
          { label: "用途", render: (r) => r.usage_category ?? "-" },
          { label: "メーカー", render: (r) => r.manufacturer ?? "-" },
        ],
      }),
      childList({
        key: "constructionMethods",
        label: "施工方法",
        detailMasterKey: "construction-method",
        rows: (r: MaterialDivisionBase, db) => activeChildrenBy(db.constructionMethods, "material_division_id", r.id),
        columns: [{ label: "施工方法名", render: (r) => r.name }],
      }),
      childList({
        key: "stirringMethods",
        label: "撹拌方法",
        detailMasterKey: "stirring-method",
        rows: (r: MaterialDivisionBase, db) => activeChildrenBy(db.stirringMethods, "material_division_id", r.id),
        columns: [{ label: "撹拌方法名", render: (r) => r.name }],
      }),
    ],
  }),

  // ---------------- 発注者 ----------------
  client: masterDef<ClientBase>({
    collection: (db) => db.clients,
    title: "発注者",
    recordTitle: (r) => r.name,
    infoFields: [
      { label: "ID", render: (r) => `#${r.id}` },
      { label: "名称", render: (r) => r.name },
    ],
    childLists: [
      childList({
        key: "works",
        label: "紐付く工事",
        detailMasterKey: "work",
        rows: (r: ClientBase, db) => activeChildrenBy(db.works, "client_id", r.id),
        columns: [
          { label: "整理番号", render: (r) => r.control_number ?? "-" },
          { label: "略称", render: (r) => r.short_name ?? "-" },
          { label: "工期", render: (r) => `${r.period_start ?? "-"} 〜 ${r.period_end ?? "-"}` },
        ],
      }),
    ],
  }),

  // ---------------- 工種 / 種別 / 工程 ----------------
  "work-type": masterDef<WorkTypeBase>({
    collection: (db) => db.workTypes,
    title: "工種",
    recordTitle: (r) => r.name,
    infoFields: [
      { label: "ID", render: (r) => `#${r.id}` },
      { label: "工種名", render: (r) => r.name },
      { label: "表示順", render: (r) => r.display_order ?? "-" },
      { label: "工事型区分", render: (r, db) => linkOr("area-division", r.area_division_id, adName(db, r.area_division_id)) },
    ],
    childLists: [
      childList({
        key: "subtypes",
        label: "種別",
        detailMasterKey: "work-subtype",
        rows: (r: WorkTypeBase, db) => activeChildrenBy(db.workSubtypes, "work_type_id", r.id),
        columns: [
          { label: "種別名", render: (r) => r.name },
          { label: "単位", render: (r) => r.unit ?? "-" },
        ],
      }),
    ],
  }),
  "work-subtype": masterDef<WorkSubtypeBase>({
    collection: (db) => db.workSubtypes,
    title: "種別",
    recordTitle: (r) => r.name,
    infoFields: [
      { label: "ID", render: (r) => `#${r.id}` },
      { label: "種別名", render: (r) => r.name },
      { label: "単位", render: (r) => r.unit ?? "-" },
      { label: "工事型区分", render: (r, db) => linkOr("area-division", r.area_division_id, adName(db, r.area_division_id)) },
      { label: "工種", render: (r, db) => linkOr("work-type", r.work_type_id, wtName(db, r.work_type_id)) },
      { label: "主工程", render: (r, db) => linkOr("work-detail", r.primary_work_detail_id, wdName(db, r.primary_work_detail_id), "-") },
    ],
    childLists: [
      childList({
        key: "details",
        label: "工程",
        detailMasterKey: "work-detail",
        rows: (r: WorkSubtypeBase, db) => activeChildrenBy(db.workDetails, "work_subtype_id", r.id),
        columns: [
          { label: "工程名", render: (r) => r.name },
          { label: "配合比", render: (r, db) => linkOr("mix-ratio", r.mix_ratio_definition_id, mixLabelOrNull(db, r.mix_ratio_definition_id) ?? "-", "(未設定)") },
        ],
      }),
    ],
  }),
  "work-detail": masterDef<WorkDetailBase>({
    collection: (db) => db.workDetails,
    title: "工程",
    recordTitle: (r) => r.name,
    infoFields: [
      { label: "ID", render: (r) => `#${r.id}` },
      { label: "工程名", render: (r) => r.name },
      { label: "工事型区分", render: (r, db) => linkOr("area-division", r.area_division_id, adName(db, r.area_division_id)) },
      { label: "種別", render: (r, db) => linkOr("work-subtype", r.work_subtype_id, wstName(db, r.work_subtype_id)) },
      { label: "配合比", render: (r, db) => linkOr("mix-ratio", r.mix_ratio_definition_id, mixLabelOrNull(db, r.mix_ratio_definition_id) ?? "-", "(未設定)") },
    ],
    childLists: [
      childList({
        key: "qualityItems",
        label: "品質測定項目",
        detailMasterKey: "quality-measurement-item",
        rows: (r: WorkDetailBase, db) => db.qualityMeasurementItems.filter(
          (q) => q.deleted_at == null && q.scope_kind === "work_detail" && q.work_detail_id === r.id,
        ),
        columns: [
          { label: "項目名", render: (r) => r.name },
          { label: "単位", render: (r) => r.unit ?? "-" },
          { label: "上下限", render: (r) => rangeOrDash(r.lower_limit, r.upper_limit) },
          { label: "判定必須", render: (r) => checkOrDash(r.judge_required) },
        ],
        empty: "この工程に紐付く品質測定項目はありません",
      }),
    ],
  }),

  // ---------------- 施工方法 / 撹拌方法 / 搬入場所 / 場所区分 / 品質測定項目 ----------------
  "construction-method": masterDef<ConstructionMethodBase>({
    collection: (db) => db.constructionMethods,
    title: "施工方法",
    recordTitle: (r) => r.name,
    infoFields: [
      { label: "ID", render: (r) => `#${r.id}` },
      { label: "施工方法名", render: (r) => r.name },
      { label: "材料区分", render: (r, db) => linkOr("material-division", r.material_division_id, mdivName(db, r.material_division_id)) },
    ],
    childLists: [
      childList({
        key: "mixRatios",
        label: "この施工方法を使う配合比",
        detailMasterKey: "mix-ratio",
        rows: (r: ConstructionMethodBase, db) => activeChildrenBy(db.mixRatioDefinitions, "construction_method_id", r.id),
        columns: [
          { label: "配合比", render: (r, db) => mixRatioDisplayName(db, r) },
          { label: "材料", render: (r, db) => linkOr("material", r.material_id, matName(db, r.material_id)) },
          { label: "撹拌方法", render: (r, db) => linkOr("stirring-method", r.stirring_method_id, smName(db, r.stirring_method_id)) },
          { label: "撹拌時間(分)", render: (r) => r.stirring_time_minutes ?? "-" },
        ],
        empty: "この施工方法を採用する配合比はまだありません",
      }),
    ],
  }),
  "stirring-method": masterDef<StirringMethodBase>({
    collection: (db) => db.stirringMethods,
    title: "撹拌方法",
    recordTitle: (r) => r.name,
    infoFields: [
      { label: "ID", render: (r) => `#${r.id}` },
      { label: "撹拌方法名", render: (r) => r.name },
      { label: "材料区分", render: (r, db) => linkOr("material-division", r.material_division_id, mdivName(db, r.material_division_id)) },
    ],
    childLists: [
      childList({
        key: "mixRatios",
        label: "この撹拌方法を使う配合比",
        detailMasterKey: "mix-ratio",
        rows: (r: StirringMethodBase, db) => activeChildrenBy(db.mixRatioDefinitions, "stirring_method_id", r.id),
        columns: [
          { label: "配合比", render: (r, db) => mixRatioDisplayName(db, r) },
          { label: "材料", render: (r, db) => linkOr("material", r.material_id, matName(db, r.material_id)) },
          { label: "施工方法", render: (r, db) => linkOr("construction-method", r.construction_method_id, cmName(db, r.construction_method_id)) },
          { label: "撹拌時間(分)", render: (r) => r.stirring_time_minutes ?? "-" },
        ],
        empty: "この撹拌方法を採用する配合比はまだありません",
      }),
    ],
  }),
  "carry-in-location": masterDef<CarryInLocationBase>({
    collection: (db) => db.carryInLocations,
    title: "搬入場所",
    recordTitle: (r) => r.name,
    infoFields: [
      { label: "ID", render: (r) => `#${r.id}` },
      { label: "搬入場所名", render: (r) => r.name },
      { label: "工事型区分", render: (r, db) => linkOr("area-division", r.area_division_id, adName(db, r.area_division_id)) },
    ],
  }),
  "location-division": masterDef<LocationDivisionBase>({
    collection: (db) => db.locationDivisions,
    title: "場所区分",
    recordTitle: (r, db) => `${slotLabelFor(db, r.area_division_id, r.slot)}: ${r.division_name}`,
    infoFields: [
      { label: "ID", render: (r) => `#${r.id}` },
      { label: "スロット", render: (r, db) => slotLabelFor(db, r.area_division_id, r.slot) },
      { label: "上位区分パス", render: (r, db) => formatParentPath(db, r.id) || "指定なし" },
      { label: "区分名", render: (r) => r.division_name },
      { label: "表示順", render: (r) => r.display_order ?? "-" },
      { label: "工事型区分", render: (r, db) => linkOr("area-division", r.area_division_id, adName(db, r.area_division_id)) },
    ],
  }),
  "quality-measurement-item": masterDef<QualityMeasurementItemBase>({
    collection: (db) => db.qualityMeasurementItems,
    title: "品質測定項目",
    recordTitle: (r) => r.name ?? `品質測定 #${r.id}`,
    infoFields: [
      { label: "ID", render: (r) => `#${r.id}` },
      { label: "項目名", render: (r) => r.name ?? "-" },
      { label: "単位", render: (r) => r.unit ?? "-" },
      { label: "工事型区分", render: (r, db) => linkOr("area-division", r.area_division_id, adName(db, r.area_division_id)) },
      { label: "対象種別", render: (r) => r.scope_kind ?? "-" },
      {
        label: "対象材料",
        render: (r, db) => {
          if (r.scope_kind === "material") {
            return linkOr("material", r.material_id, matName(db, r.material_id));
          }
          return "-";
        },
      },
      {
        label: "対象工程",
        render: (r, db) => {
          if (r.scope_kind === "work_detail") {
            return linkOr("work-detail", r.work_detail_id, wdName(db, r.work_detail_id));
          }
          return "-";
        },
      },
      { label: "上限", render: (r) => r.upper_limit ?? "-" },
      { label: "下限", render: (r) => r.lower_limit ?? "-" },
      { label: "判定必須", render: (r) => checkOrDash(r.judge_required) },
      { label: "タイミング", render: (r) => r.timing_category ?? "-" },
    ],
  }),

  // ---------------- 材料 / 材料細目 / 可使時間 / 配合比 / 配合比明細 ----------------
  material: masterDef<MaterialMasterBase>({
    collection: (db) => db.materials,
    title: "材料",
    recordTitle: (r) => r.name,
    infoFields: [
      { label: "ID", render: (r) => `#${r.id}` },
      { label: "材料名", render: (r) => r.name },
      { label: "材料区分", render: (r, db) => linkOr("material-division", r.material_division_id, mdivName(db, r.material_division_id)) },
      { label: "用途", render: (r) => r.usage_category ?? "-" },
      { label: "規格", render: (r) => r.standard_name ?? "-" },
      { label: "メーカー", render: (r) => r.manufacturer ?? "-" },
      { label: "荷姿", render: (r) => r.package_form ?? "-" },
      { label: "QR管理", render: (r) => checkOrCross(r.qr_managed) },
      { label: "湿度上限/下限", render: (r) => `${r.humidity_upper ?? "-"} / ${r.humidity_lower ?? "-"} %` },
      {
        label: "標準使用量 (参考値)",
        render: (r) => {
          if (r.standard_usage != null) {
            return `${r.standard_usage} ${r.standard_usage_unit ?? ""} ※メーカー参考値・閲覧専用`;
          }
          return "-";
        },
      },
      {
        label: "備考",
        render: (r) => {
          if (r.remarks) {
            return <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{r.remarks}</Typography>;
          }
          return "-";
        },
      },
    ],
    childLists: [
      childList({
        key: "details",
        label: "材料細目",
        detailMasterKey: "material-detail",
        rows: (r: MaterialMasterBase, db) => activeChildrenBy(db.materialDetails, "material_id", r.id),
        columns: [
          { label: "細目名", render: (r) => r.detail_name },
          {
            label: "内容量",
            render: (r) => {
              if (r.content_quantity != null) {
                return `${r.content_quantity} ${r.content_unit ?? ""}`;
              }
              return "-";
            },
          },
          { label: "荷姿", render: (r) => r.package_form ?? "-" },
          { label: "使用期限(月)", render: (r) => r.shelf_life_months ?? "-" },
        ],
      }),
      childList({
        key: "mixRatios",
        label: "配合比",
        detailMasterKey: "mix-ratio",
        rows: (r: MaterialMasterBase, db) => activeChildrenBy(db.mixRatioDefinitions, "material_id", r.id),
        columns: [
          { label: "配合比", render: (r, db) => mixRatioDisplayName(db, r) },
          { label: "施工方法", render: (r, db) => linkOr("construction-method", r.construction_method_id, cmName(db, r.construction_method_id)) },
          { label: "撹拌方法", render: (r, db) => linkOr("stirring-method", r.stirring_method_id, smName(db, r.stirring_method_id)) },
          { label: "撹拌時間(分)", render: (r) => r.stirring_time_minutes ?? "-" },
        ],
      }),
      childList({
        key: "potLifes",
        label: "可使時間",
        detailMasterKey: "pot-life",
        rows: (r: MaterialMasterBase, db) => activeChildrenBy(db.potLifeSpecs, "material_id", r.id),
        columns: [
          { label: "区間名", render: (r) => r.definition_name ?? "-" },
          { label: "温度範囲", render: (r) => rangeOrDash(r.temperature_lower, r.temperature_upper, "℃") },
          { label: "可使時間(分)", render: (r) => r.pot_life_minutes ?? "-" },
        ],
      }),
      childList({
        key: "qualityItems",
        label: "品質測定項目",
        detailMasterKey: "quality-measurement-item",
        rows: (r: MaterialMasterBase, db) => db.qualityMeasurementItems.filter(
          (q) => q.deleted_at == null && q.scope_kind === "material" && q.material_id === r.id,
        ),
        columns: [
          { label: "項目名", render: (r) => r.name },
          { label: "単位", render: (r) => r.unit ?? "-" },
          { label: "上下限", render: (r) => rangeOrDash(r.lower_limit, r.upper_limit) },
          { label: "判定必須", render: (r) => checkOrDash(r.judge_required) },
        ],
        empty: "この材料に紐付く品質測定項目はありません",
      }),
    ],
  }),
  "material-detail": masterDef<MaterialDetailBase>({
    collection: (db) => db.materialDetails,
    title: "材料細目",
    recordTitle: (r, db) => `${matName(db, r.material_id)} / ${r.detail_name}`,
    infoFields: [
      { label: "ID", render: (r) => `#${r.id}` },
      { label: "材料区分", render: (r, db) => linkOr("material-division", r.material_division_id, mdivName(db, r.material_division_id)) },
      { label: "材料", render: (r, db) => linkOr("material", r.material_id, matName(db, r.material_id)) },
      { label: "細目名", render: (r) => r.detail_name },
      {
        label: "内容量",
        render: (r) => {
          if (r.content_quantity != null) {
            return `${r.content_quantity} ${r.content_unit ?? ""}`;
          }
          return "-";
        },
      },
      { label: "荷姿", render: (r) => r.package_form ?? "-" },
      { label: "使用期限(月)", render: (r) => r.shelf_life_months ?? "-" },
      { label: "比重", render: (r) => r.specific_gravity ?? "-" },
      { label: "危険物分類", render: (r) => r.hazardous_class ?? "-" },
    ],
    childLists: [
      childList({
        key: "mixRatioUsages",
        label: "この細目を使う配合比",
        rows: (r: MaterialDetailBase, db) => db.mixRatioDetails.filter(
          (d) => d.deleted_at == null && d.material_detail_id === r.id,
        ),
        columns: [
          {
            label: "配合比",
            render: (r, db) => {
              const def = findById(db.mixRatioDefinitions, r.mix_ratio_definition_id);
              if (def) {
                return <MasterLink type="mix-ratio" id={def.id} name={mixRatioDisplayName(db, def)} />;
              }
              return "(参照先なし)";
            },
          },
          { label: "材料", render: (r, db) => linkOr("material", r.material_id, matName(db, r.material_id)) },
          {
            label: "配合比範囲",
            render: (r) => {
              if (r.ratio_end != null) {
                return `${r.ratio_start} 〜 ${r.ratio_end}`;
              }
              return `${r.ratio_start}`;
            },
          },
        ],
        empty: "この細目を構成要素として使う配合比はまだありません",
      }),
    ],
  }),
  "pot-life": masterDef<PotLifeSpecBase>({
    collection: (db) => db.potLifeSpecs,
    title: "可使時間",
    recordTitle: (r, db) => `${matName(db, r.material_id)} 可使時間 #${r.id}`,
    infoFields: [
      { label: "ID", render: (r) => `#${r.id}` },
      { label: "材料区分", render: (r, db) => linkOr("material-division", r.material_division_id, mdivName(db, r.material_division_id)) },
      { label: "材料", render: (r, db) => linkOr("material", r.material_id, matName(db, r.material_id)) },
      { label: "区間名", render: (r) => r.definition_name ?? "-" },
      { label: "温度範囲", render: (r) => rangeOrDash(r.temperature_lower, r.temperature_upper, "℃") },
      {
        label: "可使時間",
        render: (r) => {
          if (r.pot_life_minutes != null) {
            return `${r.pot_life_minutes} 分`;
          }
          return "-";
        },
      },
    ],
  }),
  "mix-ratio": masterDef<MixRatioDefinitionBase>({
    collection: (db) => db.mixRatioDefinitions,
    title: "配合比",
    recordTitle: (r, db) => `${matName(db, r.material_id)} / ${mixRatioDisplayName(db, r)}`,
    infoFields: [
      { label: "ID", render: (r) => `#${r.id}` },
      { label: "材料", render: (r, db) => linkOr("material", r.material_id, matName(db, r.material_id)) },
      { label: "配合比率", render: (r, db) => mixRatioDisplayName(db, r) },
      { label: "施工方法", render: (r, db) => linkOr("construction-method", r.construction_method_id, cmName(db, r.construction_method_id)) },
      { label: "撹拌方法", render: (r, db) => linkOr("stirring-method", r.stirring_method_id, smName(db, r.stirring_method_id)) },
      { label: "撹拌時間(分)", render: (r) => r.stirring_time_minutes ?? "-" },
      { label: "備考", render: (r) => r.remarks ?? "" },
    ],
    childLists: [
      childList({
        key: "details",
        label: "配合比明細",
        rows: (r: MixRatioDefinitionBase, db) => db.mixRatioDetails.filter(
          (d) => d.deleted_at == null && d.mix_ratio_definition_id === r.id,
        ),
        columns: [
          { label: "構成細目", render: (r, db) => linkOr("material-detail", r.material_detail_id, mdName(db, r.material_detail_id)) },
          {
            label: "配合比範囲",
            render: (r) => {
              if (r.ratio_end != null) {
                return `${r.ratio_start} 〜 ${r.ratio_end}`;
              }
              return `${r.ratio_start}`;
            },
          },
          {
            label: "単位",
            render: (r, db) => {
              const detail = findById(db.materialDetails, r.material_detail_id);
              return detail?.content_unit ?? "-";
            },
          },
        ],
      }),
    ],
  }),
};

// 品質測定項目のスコープ表示。MASTER_DEFS から参照される。
function renderQualityScope(r: QualityMeasurementItemBase, db: ExtendedDB): ReactNode {
  if (r.scope_kind === "material") {
    if (r.material_id != null) {
      return <>材料: <MasterLink type="material" id={r.material_id} name={matName(db, r.material_id)} /></>;
    }
    return "材料 (未指定)";
  }
  if (r.scope_kind === "work_detail") {
    const wd = findById(db.workDetails, r.work_detail_id);
    const wstId = r.work_subtype_id ?? wd?.work_subtype_id;
    const wst = findById(db.workSubtypes, wstId);
    const wtId = r.work_type_id ?? wst?.work_type_id;
    const wt = findById(db.workTypes, wtId);
    return <>工種単位: {wt?.name ?? "すべて"} / {wst?.name ?? "すべて"} / {wd?.name ?? "すべて"}</>;
  }
  return "-";
}

// masterKey から対応コレクションの行集合を返す(MASTER_DEFS の collection アクセサが単一情報源)。
export function collectionForMasterKey(key: string, db: ExtendedDB): readonly MasterViewRecord[] {
  const def = MASTER_DEFS[key];
  if (def === undefined) {
    return [];
  }
  return def.collection(db);
}
